import type {ReactNode} from 'react';
import {EditorialAuthorLink} from './EditorialAuthor';
import {ArrowRight, Check, ExternalLink, Gamepad2, Monitor, ShieldCheck, Sparkles, TriangleAlert} from 'lucide-react';

type MonitorForPs5ArticleProps = {
  header: ReactNode;
  footer: ReactNode;
};

const cover = '/guisite/cover-monitor-para-ps5-premium-v2.webp';
const oledImage = '/guisite/odyssey-oled-g5-550647018.webp';
const ipsImage = '/guisite/odyssey-g50d-fast-ips-official.webp';

const comparisonRows = [
  ['Definição', 'Mais alta; especialmente nítida em 32 polegadas', 'Muito boa em 27 polegadas', 'Aceitável em 24 polegadas'],
  ['Limite útil no PS5', 'Até 120 Hz', 'Até 120 Hz', 'Até 120 Hz'],
  ['Conexão decisiva', 'HDMI 2.1 com 4K a 120 Hz e VRR confirmados', 'HDMI com 1440p a 120 Hz e VRR confirmados', 'HDMI com 1080p a 120 Hz'],
  ['Melhor cenário', 'Jogos cinematográficos, tela maior e máxima qualidade', 'Mesa, 27 polegadas e melhor equilíbrio de custo', 'Orçamento curto e jogos competitivos'],
  ['Cuidado principal', 'Pagar caro por recursos que poucos jogos usam juntos', 'Confirmar VRR e 120 Hz pela entrada HDMI', 'Baixa densidade de pixels em 27 polegadas'],
];

const offers = [
  {
    badge: 'OLED · imagem premium',
    title: 'Samsung Odyssey OLED G5 27” QHD 180 Hz',
    image: oledImage,
    alt: 'Monitor Samsung Odyssey OLED G5 de 27 polegadas em imagem oficial',
    text: 'Para quem joga no PS5 e também usa PC, quer preto profundo e movimento muito limpo. No PS5, o benefício termina em 120 Hz; os 180 Hz ficam para o computador.',
    href: 'https://link.amazon/B0hNML8ME',
  },
  {
    badge: 'Fast IPS · uso misto',
    title: 'Samsung Odyssey G50D 27” QHD 180 Hz',
    image: ipsImage,
    alt: 'Monitor Samsung Odyssey G50D Fast IPS de 27 polegadas em imagem oficial',
    text: 'A opção mais equilibrada para alternar PS5, trabalho, navegação e PC. Confirme no anúncio do modelo exato a compatibilidade de 1440p, 120 Hz e VRR pela entrada HDMI.',
    href: 'https://link.amazon/B01vWFGzY',
  },
];

export function MonitorForPs5ArticlePage({header, footer}: MonitorForPs5ArticleProps) {
  return (
    <main className="site-shell article-shell monitor-for-ps5-page">
      {header}

      <article className="ps5-article monitor-for-ps5-article">
        <header className="article-hero monitor-ps5-hero">
          <div className="article-hero-copy">
            <a className="breadcrumb" href="/">GuiaSite / Para / Monitor para PS5</a>
            <p className="section-kicker">Guia por perfil · PlayStation 5</p>
            <h1>Monitor para PS5: 4K 120 Hz ou QHD? O que comprar</h1>
            <p className="article-lead">
              Para a maioria das mesas, um monitor QHD de 27 polegadas com 120 Hz e VRR confirmados pela entrada HDMI é a compra mais equilibrada. O 4K de 120 Hz vale mais em 32 polegadas, para quem prioriza definição, HDR e jogos cinematográficos. Acima de 120 Hz, o ganho existe apenas se o monitor também for usado em um PC.
            </p>
            <div className="article-meta">
              <EditorialAuthorLink />
              <span>Publicada em 2 de agosto de 2026</span>
              <span>Leitura útil: 12 min</span>
            </div>
          </div>

          <figure className="article-hero-media article-cover-media premium-photo-cover monitor-ps5-cover">
            <img fetchPriority="high" loading="eager" decoding="async" src={cover} alt="PlayStation 5 ao lado de monitor gamer em composição editorial com imagens oficiais" />
            <figcaption>Composição editorial GuiaSite com imagens oficiais do PlayStation 5 e do Samsung Odyssey OLED G5.</figcaption>
          </figure>
        </header>

        <section className="direct-answer monitor-direct-answer" aria-labelledby="resposta-direta-monitor-ps5">
          <div className="direct-answer-icon"><Gamepad2 aria-hidden="true" /></div>
          <div>
            <p className="section-kicker">Resposta direta</p>
            <h2 id="resposta-direta-monitor-ps5">QHD de 27” é o ponto de equilíbrio; 4K de 32” é a escolha premium.</h2>
            <p>
              Compre QHD 120 Hz se o PS5 fica sobre a mesa e você joga perto da tela. Escolha 4K 120 Hz se a tela será maior, se você valoriza detalhes finos e se o orçamento comporta um modelo com HDMI 2.1 bem implementado. Full HD só é a resposta certa quando o preço precisa ser o menor possível.
            </p>
          </div>
        </section>

        <div className="article-layout">
          <aside className="article-toc" aria-label="Conteúdo do guia">
            <strong>Neste guia</strong>
            <a href="#veredito-rapido">Veredito por perfil</a>
            <a href="#comparacao">4K, QHD ou Full HD</a>
            <a href="#120-hz">O limite de 120 Hz</a>
            <a href="#hdmi-vrr">HDMI 2.1, VRR e HDR</a>
            <a href="#oled-ips">OLED ou IPS</a>
            <a href="#tamanho">27 ou 32 polegadas</a>
            <a href="#modelos">Modelos para comparar</a>
            <a href="#configurar">Como configurar</a>
            <a href="#faq-monitor-ps5">Perguntas frequentes</a>
            <a href="#fontes-monitor-ps5">Fontes e método</a>
          </aside>

          <div className="article-body">
            <section id="veredito-rapido">
              <p className="section-kicker">Veredito por perfil</p>
              <h2>Qual monitor comprar para PS5 em 2026?</h2>
              <div className="monitor-profile-grid">
                <div className="monitor-profile-card monitor-profile-card--winner">
                  <span>Melhor equilíbrio</span>
                  <h3>27” QHD, 120 Hz ou mais</h3>
                  <p>É o melhor encaixe para mesa: boa densidade de pixels, menor custo que 4K premium e espaço suficiente para enxergar a tela toda sem afastar demais a cadeira.</p>
                </div>
                <div className="monitor-profile-card">
                  <span>Melhor imagem</span>
                  <h3>32” 4K, 120 Hz e HDMI 2.1</h3>
                  <p>Entrega mais definição em jogos com interface detalhada, mundos abertos e uso próximo. É a escolha premium quando o orçamento não exige sacrificar qualidade do painel.</p>
                </div>
                <div className="monitor-profile-card">
                  <span>Menor orçamento</span>
                  <h3>24” Full HD, 120 Hz ou mais</h3>
                  <p>Faz sentido para competição e economia. Em 27 polegadas, Full HD já perde nitidez perceptível para QHD, sobretudo em texto e interfaces.</p>
                </div>
              </div>
            </section>

            <section id="comparacao">
              <p className="section-kicker">Comparação essencial</p>
              <h2>4K 120 Hz, QHD 120 Hz ou Full HD 120 Hz?</h2>
              <p>
                A resolução define quantos detalhes cabem na imagem; a taxa de atualização define quantas vezes a tela consegue atualizar por segundo. No PS5, essas duas vantagens não aparecem no máximo em todos os jogos ao mesmo tempo. Muitos títulos oferecem um modo de qualidade com resolução maior e um modo de desempenho com mais quadros.
              </p>
              <div className="comparison-table-wrap">
                <table className="comparison-table monitor-resolution-table">
                  <thead><tr><th>Critério</th><th>4K 120 Hz</th><th>QHD 120 Hz</th><th>Full HD 120 Hz</th></tr></thead>
                  <tbody>
                    {comparisonRows.map(([criterion, fourK, qhd, fullHd]) => (
                      <tr key={criterion}><th>{criterion}</th><td>{fourK}</td><td>{qhd}</td><td>{fullHd}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="editorial-callout">
                <Sparkles aria-hidden="true" />
                <p><strong>Regra simples:</strong> QHD é a resolução racional para 27 polegadas. Em 32 polegadas, 4K ganha força porque mantém a imagem mais densa e permite aproveitar melhor uma tela grande vista de perto.</p>
              </div>
            </section>

            <section id="120-hz">
              <p className="section-kicker">O número que importa</p>
              <h2>O PS5 aproveita monitor de 144 Hz, 165 Hz ou 180 Hz?</h2>
              <p>
                O PS5 pode enviar até 120 quadros por segundo nos jogos compatíveis. Portanto, um monitor de 144, 165 ou 180 Hz pode funcionar muito bem, mas o console não transforma essas taxas extras em mais fluidez. Elas só trazem vantagem quando o mesmo monitor também recebe um PC capaz de gerar mais de 120 fps.
              </p>
              <p>
                Isso evita um erro comum: pagar mais por “180 Hz” e descobrir que o modelo limita a entrada HDMI, justamente a usada pelo console. A taxa anunciada pode valer apenas para DisplayPort. Antes da compra, procure no manual ou na ficha técnica a resolução e a frequência máximas <strong>via HDMI</strong>.
              </p>
              <ul className="article-check-list">
                <li><Check aria-hidden="true" /> Jogos compatíveis precisam oferecer modo de 120 fps.</li>
                <li><Check aria-hidden="true" /> O PS5 precisa estar com “Saída de 120 Hz” em automático.</li>
                <li><Check aria-hidden="true" /> O monitor precisa aceitar a combinação desejada pela porta HDMI.</li>
                <li><Check aria-hidden="true" /> O cabo e qualquer equipamento entre console e tela precisam suportar o sinal.</li>
              </ul>
              <p>
                Use a <a href="/ferramentas/calculadora-fps-hz-frame-time">calculadora de FPS, Hz e tempo de quadro</a> para visualizar por que o salto de 60 para 120 Hz é muito maior que o de 120 para 180 Hz no uso do console.
              </p>
            </section>

            <section id="hdmi-vrr">
              <p className="section-kicker">Compatibilidade real</p>
              <h2>HDMI 2.1, VRR, ALLM e HDR sem confusão</h2>
              <div className="spec-explainer-grid">
                <div><strong>HDMI 2.1</strong><p>É a rota segura para 4K a 120 Hz. Para QHD a 120 Hz, o rótulo HDMI 2.1 não é obrigatório por si só; o que decide é o modo aceito pelo monitor na entrada HDMI.</p></div>
                <div><strong>VRR</strong><p>Sincroniza a atualização da tela com a variação de quadros do jogo, reduzindo cortes e engasgos. Confirme compatibilidade com PS5, não apenas FreeSync ou G-Sync no PC.</p></div>
                <div><strong>ALLM</strong><p>Permite que a tela entre automaticamente no modo de baixa latência. É conveniente, mas não substitui uma resposta rápida nem uma configuração correta.</p></div>
                <div><strong>HDR</strong><p>O selo sozinho diz pouco. Contraste, brilho, controle de iluminação e mapeamento de tons determinam se o HDR será realmente melhor que SDR.</p></div>
              </div>
              <div className="warning-panel">
                <TriangleAlert aria-hidden="true" />
                <div><strong>Não compre apenas pela etiqueta “HDMI 2.1”.</strong><p>Leia a tabela de sinais aceitos, procure relatos com PS5 e confirme 1440p/120 Hz ou 4K/120 Hz, VRR e HDR no modelo exato. Duas telas com o mesmo conector podem oferecer combinações diferentes.</p></div>
              </div>
            </section>

            <section id="oled-ips">
              <p className="section-kicker">Tecnologia de painel</p>
              <h2>OLED ou IPS para jogar no PS5?</h2>
              <p>
                OLED entrega preto verdadeiro, contraste muito alto e resposta de pixels excepcional. Em jogos escuros, ficção científica e cenas cinematográficas, a diferença é evidente. IPS costuma custar menos, mantém ótima nitidez de texto e não exige o mesmo cuidado com elementos estáticos durante trabalho ou navegação prolongados.
              </p>
              <p>
                Para um monitor usado principalmente com o PS5, OLED é a experiência premium. Para um monitor único que passa horas com navegador, planilha, edição e depois recebe o console, IPS continua sendo a recomendação mais tranquila. Nosso comparativo completo explica <a href="/comparar/monitor-oled-vs-ips/">quando OLED ou IPS vale mais</a>.
              </p>
            </section>

            <section id="tamanho">
              <p className="section-kicker">Ergonomia visual</p>
              <h2>27 ou 32 polegadas: qual tamanho combina com cada resolução?</h2>
              <p>
                Em 27 polegadas, QHD oferece cerca de 109 pixels por polegada: um equilíbrio muito bom para jogar e trabalhar a uma distância típica de mesa. Em 32 polegadas, QHD cai para perto de 92 ppi e a malha de pixels fica mais perceptível. O 4K de 32 polegadas sobe para aproximadamente 138 ppi e preserva a nitidez.
              </p>
              <p>
                Tamanho também muda a leitura periférica. Para jogos competitivos, 24 ou 27 polegadas deixam a ação mais concentrada. Para campanha, exploração e uso com controle, 32 polegadas pode ser mais envolvente, desde que haja profundidade suficiente na mesa. Confira números exatos na <a href="/ferramentas/calculadora-de-ppi">calculadora de PPI</a>.
              </p>
            </section>

            <section className="market-context-section">
              <p className="section-kicker">Contexto de mercado</p>
              <h2>O próprio monitor PlayStation reforça a escolha QHD de 27 polegadas</h2>
              <p>
                A Sony apresentou um monitor oficial PlayStation de 27 polegadas com painel IPS QHD, VRR e suporte a até 120 Hz no PS5 e PS5 Pro. O produto chega em agosto de 2026 aos Estados Unidos e ao Japão; não há disponibilidade brasileira confirmada. Ele não é uma recomendação de compra para o Brasil neste momento, mas confirma a lógica técnica: QHD, 27 polegadas e 120 Hz formam um conjunto coerente para jogar perto da tela.
              </p>
            </section>

            <section id="modelos">
              <p className="section-kicker">Modelos para comparar</p>
              <h2>Duas opções QHD que atendem perfis diferentes</h2>
              <p>
                Os modelos abaixo já aparecem no ecossistema GuiaSite e usam imagens oficiais. Eles não são declarados “os melhores” por catálogo: servem como referências concretas de OLED e Fast IPS. Confirme sempre o código do modelo, a entrada HDMI e os modos aceitos antes de fechar a compra.
              </p>
              <div className="monitor-offer-grid">
                {offers.map((offer) => (
                  <div className="monitor-offer-card" key={offer.href}>
                    <div className="monitor-offer-media"><img src={offer.image} alt={offer.alt} loading="lazy" /></div>
                    <div className="monitor-offer-copy">
                      <span>{offer.badge}</span>
                      <h3>{offer.title}</h3>
                      <p>{offer.text}</p>
                      <a href={offer.href} target="_blank" rel="nofollow sponsored noopener noreferrer">
                        Ver preço atual na Amazon <ExternalLink size={16} aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <p className="affiliate-disclosure">Links de afiliado: o GuiaSite pode receber comissão sem custo adicional para você. A recomendação editorial não depende da loja nem da compra.</p>
            </section>

            <section id="configurar">
              <p className="section-kicker">Depois da compra</p>
              <h2>Como configurar 120 Hz e VRR no PS5</h2>
              <ol className="numbered-guide">
                <li><strong>Ligue o PS5 diretamente ao monitor.</strong><span>Evite adaptadores, capturadoras e receivers durante o primeiro teste.</span></li>
                <li><strong>Abra Configurações &gt; Tela e vídeo &gt; Saída de vídeo.</strong><span>Defina “Saída de 120 Hz” como Automático e ative VRR quando disponível.</span></li>
                <li><strong>Confira “Informações do dispositivo HDMI conectado”.</strong><span>Veja quais resoluções, frequências e recursos a tela realmente comunicou ao console.</span></li>
                <li><strong>Ative o modo de desempenho no jogo.</strong><span>Alguns títulos exigem essa seleção dentro do próprio menu gráfico.</span></li>
                <li><strong>Revise o menu do monitor.</strong><span>Adaptive Sync, modo de baixa latência ou formato HDMI ampliado podem precisar de ativação manual.</span></li>
              </ol>
            </section>

            <section>
              <p className="section-kicker">Erros que custam caro</p>
              <h2>O que evitar ao escolher monitor para PS5</h2>
              <div className="avoid-grid">
                <div><TriangleAlert aria-hidden="true" /><h3>Comprar 180 Hz só pelo número</h3><p>O PS5 para em 120 Hz. Pague a diferença apenas se o PC também usar a tela ou se o modelo for melhor em outros critérios.</p></div>
                <div><TriangleAlert aria-hidden="true" /><h3>Confiar em “HDR compatível”</h3><p>Aceitar sinal HDR não garante brilho, contraste ou impacto visual. Em painéis básicos, SDR bem ajustado pode parecer melhor.</p></div>
                <div><TriangleAlert aria-hidden="true" /><h3>Escolher ultrawide esperando tela cheia</h3><p>O PS5 trabalha com formatos tradicionais de TV. Em 21:9, espere barras laterais ou imagem esticada, conforme o monitor.</p></div>
                <div><TriangleAlert aria-hidden="true" /><h3>Esquecer o áudio</h3><p>Muitos monitores não têm alto-falantes. Planeje headset, caixa de som ou saída de áudio antes de descobrir isso na instalação.</p></div>
              </div>
            </section>

            <section id="faq-monitor-ps5">
              <p className="section-kicker">Perguntas reais</p>
              <h2>Dúvidas frequentes sobre monitor para PS5</h2>
              <div className="faq-list">
                <details><summary>PS5 funciona em monitor QHD 1440p?</summary><p>Sim. O PS5 oferece saída 1440p com HDR, 60 ou 120 Hz e VRR quando o monitor aceita esses modos pela entrada HDMI.</p></details>
                <details><summary>Precisa de HDMI 2.1 para jogar a 120 Hz no PS5?</summary><p>Para 4K a 120 Hz, HDMI 2.1 é a referência. Para QHD ou Full HD a 120 Hz, versões anteriores podem ter banda suficiente, mas o modelo precisa aceitar explicitamente o modo pela porta HDMI.</p></details>
                <details><summary>Monitor de 165 ou 180 Hz funciona no PS5?</summary><p>Funciona se for compatível com o sinal do console, mas o PS5 entrega no máximo 120 Hz. A frequência restante só é aproveitada por outro dispositivo, como um PC.</p></details>
                <details><summary>4K faz muita diferença para QHD no PS5?</summary><p>A diferença fica mais visível em telas maiores, distância curta e jogos detalhados. Em 27 polegadas sobre uma mesa, QHD já é nítido e costuma oferecer relação de custo melhor.</p></details>
                <details><summary>OLED dá burn-in jogando PS5?</summary><p>O risco existe com uso repetido e prolongado de elementos estáticos, embora proteções modernas o reduzam. Variedade de conteúdo, proteção de painel e garantia devem entrar na decisão.</p></details>
                <details><summary>Vale comprar monitor ultrawide para PS5?</summary><p>Não se o objetivo principal é usar toda a largura 21:9. O console produz imagem em formato convencional, então o monitor pode exibir barras laterais ou esticar a imagem.</p></details>
              </div>
            </section>

            <section id="fontes-monitor-ps5">
              <p className="section-kicker">Transparência editorial</p>
              <h2>Fontes e método</h2>
              <p>
                Este guia cruza documentação oficial do PlayStation, fichas oficiais dos monitores citados e cálculos de densidade e tempo de quadro. As recomendações por tamanho e perfil são conclusões editoriais do GuiaSite. Não usamos preço temporário como fato permanente e não tratamos taxa máxima por DisplayPort como desempenho disponível no PS5.
              </p>
              <ul className="source-list">
                <li><a href="https://www.playstation.com/pt-br/support/hardware/ps5-4k-resolution-guide/" target="_blank" rel="noopener noreferrer">PlayStation: resoluções, 120 Hz, VRR e HDMI no PS5</a></li>
                <li><a href="https://blog.playstation.com/2026/07/27/playstations-27-gaming-monitor-launches-august-27/" target="_blank" rel="noopener noreferrer">PlayStation.Blog: lançamento do monitor PlayStation de 27 polegadas</a></li>
                <li><a href="https://blog.playstation.com/2025/11/11/first-look-at-playstations-27-gaming-monitor/" target="_blank" rel="noopener noreferrer">PlayStation.Blog: especificações do monitor PlayStation</a></li>
                <li><a href="https://www.samsung.com/br/monitors/gaming/odyssey-oled-g5-g50sf-27-inch-180hz-oled-qhd-ls27fg500slmzd/" target="_blank" rel="noopener noreferrer">Samsung Brasil: Odyssey OLED G5 de 27 polegadas</a></li>
                <li><a href="https://www.samsung.com/us/monitors/gaming/27-inch-odyssey-g5-g50d-180hz-ips-qhd-sku-ls27dg500enxza/" target="_blank" rel="noopener noreferrer">Samsung: Odyssey G50D Fast IPS de 27 polegadas</a></li>
              </ul>
              <div className="method-stamp"><ShieldCheck aria-hidden="true" /><div><strong>Última revisão: 2 de agosto de 2026</strong><span>Metodologia GuiaSite Score 1.1 · compatibilidade, imagem, custo relativo e adequação por perfil.</span></div></div>
            </section>

            <section className="final-verdict" id="veredito-monitor-ps5">
              <p className="section-kicker">Veredito GuiaSite</p>
              <h2>O melhor monitor para PS5 não é o de maior número; é o que entrega 120 Hz, VRR e boa imagem no tamanho certo.</h2>
              <p>
                Para a maioria, a recomendação é 27 polegadas, QHD, 120 Hz ou mais e compatibilidade comprovada com VRR pela HDMI. Para uma experiência premium em 32 polegadas, 4K 120 Hz com HDMI 2.1 ganha em nitidez e longevidade visual. Full HD permanece como solução econômica, preferencialmente em 24 polegadas. Entre OLED e IPS, escolha a prioridade: OLED para impacto visual; IPS para versatilidade e tranquilidade no uso misto.
              </p>
              <a className="verdict-next-link" href="/comparar/monitor-oled-vs-ips/">Comparar OLED e IPS em detalhes <ArrowRight size={17} aria-hidden="true" /></a>
            </section>
          </div>
        </div>
      </article>

      {footer}
    </main>
  );
}



